import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Grid,
  Pagination,
  PaginationItem,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';

import Canvas from '../../redux/containers/visualizer/Canvas';
import DetectionModal from '../../redux/containers/visualizer/DetectionModal';
import TopMenu from '../../redux/containers/visualizer/TopMenu';
import {
  buildDownloadContext,
  consolidationStagesOptions,
  filterShelfoutPositions,
  handleGetPredictionsColor,
  READING_AI_TYPES,
  COLLECTOR_AI_TYPES,
  SHELFOUT_POSITION_SCOPES,
  SHELFOUT_POSITION_SOURCES,
} from '../tools/Canvas/utils';
import ConsolidationItemModal from './ConsolidationItemModal';

const READING_TO_DETECTION_MAP = {
  'Label Reading': 'Label Detection',
  'Promotional Signage': 'Label Detection',
  'Promotional Signage Reading': 'Label Detection',
  'Promotional Signage Detection': 'Label Detection',
  'Product Detection': 'Product Detection',
  'Bay Label Detection': 'Bay Label Detection',
  'Bay Label Reading': 'Bay Label Detection',
};

const DETECTION_TO_READING_MAP = {
  'Label Detection': 'Label Reading',
  'Label Reading': 'Label Reading',
  'Promotional Signage': 'Label Reading',
  'Product Detection': 'Product Detection Reading',
  'Shelfout Detection': 'Shelfout Detection',
  'Promotional Signage Detection': 'Promotional Signage Reading',
  'Promotional Signage Reading': 'Promotional Signage Reading',
  'Bay Label Detection': 'Bay Label Reading',
  'Bay Label Reading': 'Bay Label Reading',
};

export default function Visualizer(props) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { inputBackground = 'white', Inputcolor, consolidationStages, getConsolidationStages } = props;
  const lightBackground = inputBackground
    ? inputBackground
    : Object.values(theme.palette[inputBackground])[0];
  const styles = {
    paper: {
      width: '100%',
      height: '100%',
      padding: 2,
      textAlign: 'center',
      background:
        theme.palette.type === 'light'
          ? lightBackground
          : theme.palette.paper.main,
      color: Inputcolor
        ? Inputcolor
        : theme.palette.type === 'light'
          ? theme.palette.primary.main + 'D9'
          : 'rgba(255, 255, 255, 0.87)',
    },
  };

  const [currencyFormat, setCurrencyFormat] = useState({
    currency_symbol: null,
    currency_thousands_sep: null,
    currency_decimal_sep: null,
    currency_decimals: null,
  });
  const [canShowSecondStage, setCanShowSecondStage] = useState(false);
  const [stage, setStage] = useState('results');
  const [tabValue, setTabValue] = useState('Options');
  const [positionSource, setPositionSource] = useState(SHELFOUT_POSITION_SOURCES.ALGORITHM);
  const [positionScope, setPositionScope] = useState(SHELFOUT_POSITION_SCOPES.SHELFOUTS);
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [showLabels, setShowLabels] = useState(true);
  const [showPromotions, setShowPromotions] = useState(true);
  const [showVertexNumbers, setShowVertexNumbers] = useState(false);
  const [format, setFormat] = useState(true);
  const [showDetection, setShowDetection] = useState(true);
  const [showConsolidated, setShowConsolidated] = useState(true);
  const [opacity, setOpacity] = useState(0.5);
  const [sideView, setSideView] = useState(true);
  const [page, setPage] = useState(1);
  const [preMetadata, setPreMetadata] = useState(null);
  const [typographyLabels, setTypographyLabels] = useState([]);
  const [predictionColors, setPredictionColors] = useState({});
  const [selectedConsolidationItem, setSelectedConsolidationItem] = useState(null);
  const [isConsolidationModalOpen, setIsConsolidationModalOpen] = useState(false);

  const [formState, setFormState] = useState({
    country: null,
    chain: null,
    store: null,
    aisle: null,
    session: null,
    ia: null,
    detection: '',
    capture: '',
    sku: '',
    type: null,
    index: 0,
    showAll: true,
    limit: '100',
    auxSession: null,
    collector: 'all',
    layoutId: null,
    consolidationStage: [],
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleStageChange = (event) => {
    setStage(event.target.value);
  };

  const handleIndexChange = (event, value) => {
    if (props.images && props.images.image) {
      props.cleanImage();
      props.signImage({
        store: formState.store,
        blob_path: props.images.image[value - 1].image_url,
      });
    }
    setPage(value);
    setFormState({
      ...formState,
      index: value - 1,
    });
  };

  const handleClose = () => {
    setOpen(false);
    props.cleanSecondaryImages();
    props.cleanSecondaryImage();
  };

  const handleOpen = (target_id, type, preMetadata) => {
    setPreMetadata(preMetadata);
    setOpen(true);
    props.cleanSecondaryImages();
    props.cleanSecondaryImage();
    let aiType = '';
    if (type === 'capture') {
      aiType = READING_TO_DETECTION_MAP[formState.ia] ?? 'Overhead Inventory Box Detection';
      props.getSecondaryImages({
        store: formState.store,
        session: formState.session,
        object_type: formState.type,
        ia_type: aiType,
        capture_id: target_id,
        filter: false,
        rows_limit: formState.limit,
        aisles: [handleGetAisleName()],
      });
    } else if (type === 'detection') {
      aiType = DETECTION_TO_READING_MAP[formState.ia] ?? 'Overhead Inventory Box Reading';
      props.getSecondaryImages({
        store: formState.store,
        session: formState.session,
        object_type: formState.type,
        ia_type: aiType,
        detection_id: target_id,
        filter: false,
        rows_limit: formState.limit,
        aisles: [handleGetAisleName()],
      });
    } else if (type === 'product_detection_id') {
      props.getSecondaryImages({
        store: formState.store,
        session: formState.session,
        object_type: formState.type,
        ia_type: 'Product Detection Reading',
        capture_id: target_id.capture_id,
        product_detection_id: target_id.product_detection_id,
        filter: false,
        rows_limit: formState.limit,
        aisles: [handleGetAisleName()],
      });
    }
  };

  const handleGetAisleName = () => {
    const imageInfo = props?.images?.image?.[formState?.index];
    const imageAisleName = imageInfo?.aisle;
    const formAisleName = formState?.aisle;
    const baseAisleName = props?.images?.metadata?.[imageInfo?.id]?.[0]?.aisle;

    return imageAisleName || baseAisleName || formAisleName || '-';
  };

  const fetchImageForDetectionId = (detectionId, type) => {
    const params = {
      store: formState.store,
      session: formState.session,
      detection_id: detectionId,
      type: type,
    }
    if (type === 'promotion') props.getPromotionalImage(params)
    else props.getLabelImage(params)
  }

  const handleConsolidationClick = (item) => {
    setSelectedConsolidationItem(item);
    setIsConsolidationModalOpen(true);
    fetchImageForDetectionId(item.id, item.type)
  };

  const handleCloseConsolidationModal = () => {
    setIsConsolidationModalOpen(false);
    setSelectedConsolidationItem(null);
  };


  useEffect(() => {
    props.getMetadataDescription()
  }, [])


  useEffect(() => {
    if (!['Consolidation', 'Product Detection'].includes(formState.ia) &&
      props.images &&
      props.images.image &&
      props.images.metadata &&
      props.images.image[formState.index] &&
      props.images.image[formState.index].id
    ) {
      const newPredictionColors = handleGetPredictionsColor({
        predictions: props.images.metadata[props.images.image[formState.index]?.id],
        stage,
      });
      setPredictionColors(newPredictionColors);
      setTypographyLabels(Object.values(newPredictionColors));
    } else if (formState.ia === 'Product Detection') {
      const newPredictionColors = [
        {
          name: 'Consolidation',
          color: 'Yellow',
          id: 'consolidated_products',
        }, {
          name: 'Detection',
          color: "#ff7596",
          id: 'all_detected_products',
        }
      ]
      setPredictionColors(newPredictionColors);
      setTypographyLabels(Object.values(newPredictionColors));
    }

  }, [props.images, stage, formState.index]);

  const consolidationMetadata = useMemo(() => {
    if (formState.ia !== 'Consolidation' || !consolidationStages) return null;

    const selectedStages = formState.consolidationStage || [];
    const metadataByStage = {};

    selectedStages.forEach(stageName => {
      metadataByStage[stageName] = [];
      ['label', 'promotion'].forEach(itemType => {
        if (consolidationStages[itemType][stageName]) {
          const stageOption = consolidationStagesOptions.find(opt => opt.name === stageName);
          const stageDataWithColor = consolidationStages[itemType][stageName].map(item => ({
            ...item,
            color: stageOption?.color,
            type: itemType,
          }));
          metadataByStage[stageName] = [
            ...metadataByStage[stageName],
            ...stageDataWithColor,
          ];
        }
      });
    });
    return metadataByStage;
  }, [formState.ia, formState.consolidationStage, consolidationStages]);

  const allPredictions = useMemo(() => {
    if (formState.ia === 'Consolidation' && consolidationStages) {
      return [
        ...(consolidationStages?.label?.all_predictions ?? []),
        ...(consolidationStages?.promotion?.all_predictions ?? []),
      ];
    }
    return [];
  }, [formState.ia, consolidationStages]);

  const filteredConsolidationMetadata = useMemo(() => {
    if (!consolidationMetadata) return {};

    const filteredData = {};
    Object.keys(consolidationMetadata).forEach(stageName => {
      filteredData[stageName] = consolidationMetadata[stageName].filter(item => {
        if (item.type === 'label' && showLabels) return true;
        if (item.type === 'promotion' && showPromotions) return true;
        return false;
      });
    });
    return filteredData;
  }, [consolidationMetadata, showLabels, showPromotions]);

  const currentImageMetadata = props.images?.metadata?.[props.images?.image?.[formState.index]?.id];
  const shelfoutPositions = useMemo(
    () => filterShelfoutPositions(currentImageMetadata, positionSource, positionScope),
    [currentImageMetadata, positionSource, positionScope],
  );

  const downloadContext = useMemo(
    () => buildDownloadContext({
      ai: formState.ia,
      store: formState.store,
      selectedSession: formState.session,
      images: props.images,
      imagesSession: props.imagesSession,
      imageIndex: formState.index,
      consolidationStages,
    }),
    [formState.ia, formState.store, formState.session, props.images, props.imagesSession, formState.index, consolidationStages],
  );

  const filterProductDetections = (imageMetadata = []) => {
    if (!showConsolidated) return imageMetadata.filter(label => label['consolidation.consolidation_row_key']?.includes(label.product_detection_id));
    return imageMetadata;
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <Grid container spacing={2} sx={{ position: 'absolute' }}>
        <ConsolidationItemModal
          open={isConsolidationModalOpen}
          onClose={handleCloseConsolidationModal}
          item={selectedConsolidationItem}
          labelImage={selectedConsolidationItem?.type === 'promotion' ? props.promotionalImage : props.labelImage}
          isLoadingLabelImage={selectedConsolidationItem?.type === 'promotion' ? props.isLoadingPromotionalImage : props.isLoadingLabelImage}
          allPredictions={allPredictions}
          predictionTypes={props.metadataDescription?.prediction_types}
        />
        <DetectionModal
          open={open}
          onClose={handleClose}
          selectedLabel={selectedLabel}
          store={formState.store}
          currencyFormat={currencyFormat}
          formatCurrency={format}
          aisle={
            formState.ia === 'Shelfout Detection' &&
              props.images &&
              props.images.image &&
              props.images.image[formState.index]
              ? props.images.image[formState.index].id
              : null
          }
          ai={formState.ia}
          preMetadata={preMetadata}
        />
        <Grid item xs={12}>
          <TopMenu
            formState={formState}
            setFormState={setFormState}
            format={format}
            setFormat={setFormat}
            showDetection={showDetection}
            setShowDetection={setShowDetection}
            showConsolidated={showConsolidated}
            setShowConsolidated={setShowConsolidated}
            showLabels={showLabels}
            setShowLabels={setShowLabels}
            showPromotions={showPromotions}
            setShowPromotions={setShowPromotions}
            showVertexNumbers={showVertexNumbers}
            setShowVertexNumbers={setShowVertexNumbers}
            sideView={sideView}
            setSideView={setSideView}
            currencyFormat={currencyFormat}
            setCurrencyFormat={setCurrencyFormat}
            selectedLabel={selectedLabel}
            opacity={opacity}
            setOpacity={setOpacity}
            page={page}
            setPage={setPage}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            setSelectedLabel={setSelectedLabel}
            canShowSecondStage={canShowSecondStage}
            setCanShowSecondStage={setCanShowSecondStage}
            stage={stage}
            setStage={setStage}
            tabValue={tabValue}
            handleTabChange={handleTabChange}
            handleStageChange={handleStageChange}
            positionSource={positionSource}
            setPositionSource={setPositionSource}
            positionScope={positionScope}
            setPositionScope={setPositionScope}
            consolidationStagesOptions={consolidationStagesOptions}
            consolidationStages={consolidationStages}
            getConsolidationStages={getConsolidationStages}
          />
        </Grid>
        <Grid item xs={12}>
          <Paper sx={styles.paper}>
            <Grid container justifyContent="center" alignItems="center" spacing={2}>
              <Grid item xs={READING_AI_TYPES.includes(formState.ia) && sideView ? 6 : 12}>
                <div id="wallCanvasContainer" className={'wallCanvasContainer'}>
                  {(formState.ia === 'Consolidation' && props.image_url) ||
                    (props.images?.image?.[formState.index]?.id && props.images?.metadata &&
                      props.image_url) ? (
                    <Canvas
                      ai={formState.ia}
                      imageType={formState.type}
                      metadata={showDetection
                        ? (formState.ia === 'Consolidation'
                          ? filteredConsolidationMetadata
                          : formState.ia === 'Product Detection'
                            ? filterProductDetections(props.images?.metadata?.[props.images?.image?.[formState.index]?.id])
                            : formState.ia === 'Shelfout Detection'
                              ? shelfoutPositions
                              : props.images?.metadata?.[props.images?.image?.[formState.index]?.id])
                        : []}
                      image_url={props.image_url}
                      downloadContext={downloadContext}
                      currencyFormat={currencyFormat}
                      canShowSecondStage={canShowSecondStage}
                      stage={stage}
                      tabValue={tabValue}
                      formatCurrency={format}
                      handleOpen={handleOpen}
                      handleConsolidationClick={handleConsolidationClick}
                      setSelectedLabel={setSelectedLabel}
                      id={props.images?.image?.[formState.index]?.id}
                      showPromotions={showPromotions}
                      showLabels={showLabels}
                      showVertexNumbers={showVertexNumbers}
                      defaultZoom={
                        openMenu
                          ? READING_AI_TYPES.includes(formState.ia)
                            ? 50
                            : 50
                          : 60
                      }
                      splitView={READING_AI_TYPES.includes(formState.ia) && sideView}
                      factor={READING_AI_TYPES.includes(formState.ia) ? 2 : 1}
                      predictionColors={predictionColors}

                    />
                  ) : (
                    <Canvas
                      image={''}
                      ai={formState.ia}
                      imageType={formState.type}
                      metadata={[]}
                      defaultZoom={
                        openMenu
                          ? READING_AI_TYPES.includes(formState.ia)
                            ? 50
                            : 50
                          : 60
                      }
                      splitView={READING_AI_TYPES.includes(formState.ia) && sideView}
                      canShowSecondStage={canShowSecondStage}
                      stage={stage}
                      tabValue={tabValue}
                      showVertexNumbers={showVertexNumbers}
                      predictionColors={predictionColors}
                    />
                  )
                  }
                </div>
              </Grid>
              {READING_AI_TYPES.includes(formState.ia) && sideView && (
                  <Grid item xs={6}>
                    <div
                      id="wallCanvasContainer"
                      className={'wallCanvasContainer'}>
                      {(formState.ia === 'Consolidation' && props.image_url) ||
                        (props.images?.image?.[formState.index]?.id && props.images?.metadata &&
                          props.image_url) ? (
                        <Canvas
                          ai={formState.ia}
                          imageType={formState.type}
                          metadata={
                            props.images.metadata[
                            props.images?.image?.[formState.index]?.id
                            ]
                          }
                          image_url={props.image_url}
                          downloadContext={downloadContext}
                          currencyFormat={currencyFormat}
                          formatCurrency={format}
                          handleOpen={handleOpen}
                          setSelectedLabel={setSelectedLabel}
                          id={props.images?.image?.[formState.index]?.id}
                          imageCanvas={'ReadingCanvas'}
                          canvasContainer={'ReadingCanvasContainer'}
                          readingCanvas={true}
                          splitView={true}
                          factor={2}
                          opacity={opacity}
                          chain={formState.chain}
                          defaultZoom={
                            openMenu
                              ? READING_AI_TYPES.includes(formState.ia)
                                ? 50
                                : 50
                              : 60
                          }
                          canShowSecondStage={canShowSecondStage}
                          stage={stage}
                          tabValue={tabValue}
                          showVertexNumbers={showVertexNumbers}
                          predictionColors={predictionColors}
                        />
                      ) : (
                        <Canvas
                          image={''}
                          ai={formState.ia}
                          imageType={formState.type}
                          metadata={[]}
                          readingCanvas={true}
                          splitView={true}
                          imageCanvas={'ReadingCanvas'}
                          canvasContainer={'ReadingCanvasContainer'}
                          defaultZoom={
                            openMenu
                              ? READING_AI_TYPES.includes(formState.ia)
                                ? 50
                                : 50
                              : 60
                          }
                          canShowSecondStage={canShowSecondStage}
                          stage={stage}
                          tabValue={tabValue}
                          showVertexNumbers={showVertexNumbers}
                          predictionColors={predictionColors}
                        />
                      )}
                    </div>
                  </Grid>
                )}
            </Grid>
            <Grid container alignItems='center' sx={{ mt: 1 }}>
              <Grid item xs={3}>
                <Typography variant="body2" align="left" sx={{ marginTop: 1 }}>
                  {props.images &&
                    props.images.image &&
                    props.images.image[formState.index] &&
                    props.images.image[formState.index].id
                    ? READING_AI_TYPES.includes(formState.ia)
                      ? `Detection ID: ${props.images.image[formState.index].id
                      }`
                      : formState.ia !== 'Shelfout Detection'
                        ? `Capture ID: ${props.images.image[formState.index].id}`
                        : ''
                    : ''}
                </Typography>
                {props?.images?.metadata &&
                  props?.images?.image?.[formState.index]?.id && (
                    <Typography
                      variant="body2"
                      align="left"
                      sx={{ marginTop: 1 }}>
                      Aisle: {handleGetAisleName()}
                    </Typography>
                  )}
              </Grid>
              <Grid container item xs={6} justifyContent='center'>
                <Pagination
                  size='small'
                  hidden={!props.images || !props.images.image || props.images.image.length === 0}
                  count={props.images && props.images.image ? props.images.image.length : 0}
                  page={page}
                  siblingCount={useMediaQuery(theme.breakpoints.up('lg')) ? 4 : 0}
                  color="primary"
                  onChange={handleIndexChange}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.grey[500],
                    },
                    '& .Mui-selected': {
                      color: 'white',
                    },
                  }}
                  renderItem={item => (
                    <Tooltip
                      title={
                        props.images &&
                          props.images.image &&
                          props.images.image[item.page - 1]
                          ? props.images.image[item.page - 1].id
                          : ''
                      }>
                      <PaginationItem
                        {...item}
                        component="button"
                        color="primary"
                      />
                    </Tooltip>
                  )}
                />
                <Typography variant="body2" align="center">
                  {!formState.ia ? t('hip_app.general.no_filter') : ''}
                </Typography>
                <Typography variant="caption" align="center" sx={{ pt: 2 }}>
                  {props?.images?.images_without_info
                    ? `Images without data: ${props.images.images_without_info}`
                    : ''}
                </Typography>
              </Grid>
              <Grid container item xs={3} flexDirection='column' alignItems='flex-end'>
                <Grid item>
                  <Typography variant="body2" align="right" sx={{ marginTop: 1 }}>
                    {props.images?.image?.[formState.index]?.id &&
                      formState.type === 'Shelf' &&
                      COLLECTOR_AI_TYPES.includes(formState.ia)
                      ? `${t('hip_app.general.collector')}: ${props.images.image[formState.index].source
                      }`
                      : ''}
                  </Typography>
                </Grid>
                {/* Color legend */}
                <Grid container spacing={2} justifyContent='flex-end' mt={0}>
                  {
                    typographyLabels.length > 0 ?
                      typographyLabels.map((typographyLabel) => (
                        <Grid item key={`typographyLabels-${typographyLabel.id}`}>
                          <Grid container spacing={1} alignItems='center'>
                            <Box sx={{ width: '0.8em', height: '0.8em', borderRadius: 1, bgcolor: typographyLabel.color, mr: 1 }} />
                            <Typography variant='caption'>{typographyLabel.name}</Typography>
                          </Grid>
                        </Grid>
                      ))
                      : null
                  }
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
}
